import { Link } from "react-router";
import { cx } from "../ui.tsx";
import type { GuardNoteView } from "../lib/guard.ts";

/**
 * A guard block or ask in the run transcript (docs/specs/command-guard.md §4.5): a compact red row
 * for a block, amber for an ask, and a quiet grey row in audit mode, where the command still ran.
 */
export function GuardNoteRow({ note }: { note: GuardNoteView }) {
  const tone = note.audit ? "neutral" : note.verdict;
  const label = note.audit ? `Audit · would ${note.verdict} (allowed)` : note.verdict === "block" ? "Blocked" : "Asked";
  return (
    <li
      data-testid="guard-note"
      data-verdict={note.verdict}
      data-mode={note.audit ? "audit" : "enforce"}
      className={cx(
        "rounded-2xl border-l-2 px-3 py-2 text-[13px]",
        tone === "block" && "border-high bg-high/[0.08]",
        tone === "ask" && "border-[color:var(--medium)] bg-signal-soft",
        tone === "neutral" && "border-rule bg-ink/[0.03]",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={cx("font-medium", tone === "block" && "text-high", tone === "ask" && "text-[color:var(--medium)]", tone === "neutral" && "text-ink-2")} data-testid="guard-note-label">
          {label}
        </span>
        {(note.rules.length ? note.rules : ["guard"]).map((rule) => (
          <code key={rule} data-testid="guard-note-rule" className="rounded-md bg-ink/[0.06] px-1.5 py-0.5 font-mono text-[12px] text-ink-2">
            {rule}
          </code>
        ))}
        {note.decisionId && (
          <Link to={`/inbox?decision=${encodeURIComponent(note.decisionId)}`} data-testid="guard-note-decision" className="ml-auto text-ink-2 underline decoration-rule hover:text-ink">
            Decision
          </Link>
        )}
      </div>
      {note.command && (
        <code data-testid="guard-note-command" title={note.command} className="mt-1.5 block truncate font-mono text-[12.5px] text-ink">
          {note.command}
        </code>
      )}
      {note.reason && (
        <p data-testid="guard-note-reason" className="mt-1 text-ink-2">
          {note.reason}
        </p>
      )}
    </li>
  );
}
